import { Inject, Injectable, Optional } from '@angular/core';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import moment from 'moment';

function range<T>(length: number, valueFunction: (index: number) => T) {
	const valuesArray = Array.from<T>({ length });
	for (let i = 0; i < length; i++) {
		valuesArray[i] = valueFunction(i);
	}
	return valuesArray;
}

@Injectable({ providedIn: 'root' })
export class AppDateAdapter extends DateAdapter<Date> {
	private _localeData: {
		firstDayOfWeek: number;
		longMonths: string[];
		shortMonths: string[];
		dates: string[];
		longDaysOfWeek: string[];
		shortDaysOfWeek: string[];
		narrowDaysOfWeek: string[];
	};

	constructor(@Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string) {
		super();
		const locale = dateLocale || moment.locale();
		super.setLocale(locale);
		this._localeData = this.getLocaleData(locale);
	}

	override setLocale(locale: string) {
		super.setLocale(locale);
		this._localeData = this.getLocaleData(locale);
	}

	getYear(date: Date) {
		return moment(date).year();
	}

	getMonth(date: Date) {
		return moment(date).month();
	}

	getDate(date: Date) {
		return moment(date).date();
	}

	getDayOfWeek(date: Date) {
		return moment(date).day();
	}

	getMonthNames(style: 'long' | 'short' | 'narrow') {
		return style === 'long' ? this._localeData.longMonths : this._localeData.shortMonths;
	}

	getDateNames() {
		return this._localeData.dates;
	}

	getDayOfWeekNames(style: 'long' | 'short' | 'narrow') {
		if (style === 'long') {
			return this._localeData.longDaysOfWeek;
		}
		if (style === 'short') {
			return this._localeData.shortDaysOfWeek;
		}
		return this._localeData.narrowDaysOfWeek;
	}

	getYearName(date: Date) {
		return moment(date).format('YYYY');
	}

	getFirstDayOfWeek() {
		return this._localeData.firstDayOfWeek;
	}

	getNumDaysInMonth(date: Date) {
		return moment(date).daysInMonth();
	}

	clone(date: Date) {
		return moment(date).clone().toDate();
	}

	createDate(year: number, month: number, date: number) {
		if (month < 0 || month > 11) {
			throw new Error(`Invalid month index "${ month }". Month index has to be between 0 and 11.`);
		}

		if (date < 1) {
			throw new Error(`Invalid date "${ date }". Date has to be greater than 0.`);
		}

		const result = moment({ year, month, date }).locale(<string> this.locale);

		if (!result.isValid()) {
			throw new Error(`Invalid date "${ date }" for month with index "${ month }".`);
		}

		return result.toDate();
	}

	today() {
		return moment().locale(<string> this.locale).toDate();
	}

	parse(value: Date | string, parseFormat: string | string[]) {
		if (value && typeof value === 'string') {
			return moment(value, parseFormat, <string> this.locale, true).toDate();
		}
		return value ? moment(value).locale(<string> this.locale).toDate() : null;
	}

	format(date: Date, displayFormat: string) {
		const dateCloned = this.clone(date);
		if (!this.isValid(dateCloned)) {
			throw new Error('MomentDateAdapter: Cannot format invalid date.');
		}
		return moment(dateCloned).locale(<string> this.locale).format(displayFormat);
	}

	addCalendarYears(date: Date, years: number) {
		return moment(date).add({ years }).toDate();
	}

	addCalendarMonths(date: Date, months: number) {
		return moment(date).add({ months }).toDate();
	}

	addCalendarDays(date: Date, days: number) {
		return moment(date).add({ days }).toDate();
	}

	toIso8601(date: Date) {
		return moment(date).format();
	}

	fromIso8601(iso8601String: string) {
		const d = moment(iso8601String, moment.ISO_8601).locale(<string> this.locale).toDate();
		return this.isValid(d) ? d : null;
	}

	isDateInstance(obj: unknown) {
		return obj instanceof Date;
	}

	isValid(date: Date) {
		return moment(date).isValid();
	}

	invalid() {
		return new Date(Number.NaN);
	}

	private getLocaleData(locale: string) {
		const momentLocaleData = moment.localeData(locale);
		return {
			firstDayOfWeek: momentLocaleData.firstDayOfWeek(),
			longMonths: momentLocaleData.months(),
			shortMonths: momentLocaleData.monthsShort(),
			dates: range(31, i => moment(this.createDate(2017, 0, i + 1)).format('D')),
			longDaysOfWeek: momentLocaleData.weekdays(),
			shortDaysOfWeek: momentLocaleData.weekdaysShort(),
			narrowDaysOfWeek: momentLocaleData.weekdaysMin(),
		};
	}
}
